import React, { useState, useEffect } from "react"; // Import useEffect
import styles from './.FilterBoxComponents.module.css';
import headcountStyles from './CompanyHeadcountFilter.module.css'; // Reusing styles

const MIN_YEAR = 1800;
// Use current year dynamically for MAX_YEAR or a fixed future year if preferred
const MAX_YEAR = new Date().getFullYear(); // Or keep 2030 if you want a fixed future limit

const FoundedFilter = ({ founded, setFounded }) => {
  // Helper function to derive local state format from the founded prop
  const getLocalStateFromProp = (propValue) => {
    // Default to [null, null] if prop isn't a valid array initially
    const validProp = Array.isArray(propValue) && propValue.length === 2 ? propValue : [null, null];
    return validProp.map(e => (e === null ? "" : String(e)));
  };

  // Initialize local state using the helper, handling potential undefined/null initial prop
  const [localFounded, setLocalFounded] = useState(() => getLocalStateFromProp(founded));

  // Effect to synchronize local state when the founded prop changes from the parent
  useEffect(() => {
    const updatedLocalFounded = getLocalStateFromProp(founded);
    setLocalFounded((prev) => {
      const currentLocal = Array.isArray(prev) ? prev : ["", ""];
      if (updatedLocalFounded[0] === currentLocal[0] && updatedLocalFounded[1] === currentLocal[1]) {
        return prev;
      }
      return updatedLocalFounded;
    });
    // Dependency array: run this effect when the 'founded' prop changes
  }, [founded]);

  const handleChange = (e, index) => {
    const raw = e.target.value; // No commas for year
    const cleaned = raw.replace(/[^\d]/g, "").slice(0, 4); // Limit to 4 digits
    // Ensure localFounded is treated as an array even if it becomes undefined momentarily
    const currentLocal = Array.isArray(localFounded) ? localFounded : ["", ""];
    const updated = [...currentLocal];
    updated[index] = cleaned;
    setLocalFounded(updated);
  };

  // Keep clampYear helper
  const clampYear = (year) => {
    if (year === null || isNaN(year)) return null;
    // Ensure year is within reasonable bounds after parsing
    const parsedYear = parseInt(year, 10);
    if (parsedYear < MIN_YEAR || parsedYear > MAX_YEAR) return null; // Or clamp differently if needed
    return parsedYear;
  };

  // Updated handleBlur incorporating both fixes
  const handleBlur = () => {
    // Ensure localFounded is treated as an array
    const currentLocal = Array.isArray(localFounded) ? localFounded : ["", ""];
    let [minStr, maxStr] = currentLocal;

    // Default to empty string if undefined/null before checking emptiness
    minStr = minStr ?? "";
    maxStr = maxStr ?? "";

    const rawMin = minStr === "" ? null : Number(minStr);
    const rawMax = maxStr === "" ? null : Number(maxStr);

    // Clamp the raw numbers to valid years
    const clampedMin = clampYear(rawMin);
    let clampedMax = clampYear(rawMax);

    // Final values after potential adjustment
    let finalMin = clampedMin;
    let finalMax = clampedMax;

    // If both are valid years and max < min, adjust max to be equal to min
    if (finalMin !== null && finalMax !== null && finalMax < finalMin) {
      finalMax = finalMin;
    }

    // --- FIX 2: Determine correct parent state format (empty array vs [null, null]) ---
    let nextParentState;
    if (finalMin === null && finalMax === null) {
        nextParentState = []; // Use empty array if both are null/cleared
    } else {
        nextParentState = [finalMin, finalMax];
    }

    // Get current parent state, default to [] if not an array for comparison
    const currentParentState = Array.isArray(founded) ? founded : [];

    // Check if the state actually needs to change by comparing length and elements
    let needsUpdate = false;
    if (nextParentState.length !== currentParentState.length) {
        needsUpdate = true;
    } else if (nextParentState.length === 2) {
        // Only compare elements if both arrays have length 2
        if (nextParentState[0] !== currentParentState[0] || nextParentState[1] !== currentParentState[1]) {
            needsUpdate = true;
        }
    }
    // If both lengths are 0, needsUpdate remains false (no change needed)

    // Update parent state only if the calculated state is different
    if (needsUpdate) {
        setFounded(nextParentState);
    }
    // --- END FIX 2 ---


    // Update local state based on the final validated/adjusted values, ensuring strings
    const nextLocalState = [
        finalMin === null ? "" : String(finalMin),
        finalMax === null ? "" : String(finalMax)
    ];
    // Only update local state if it differs, prevents re-render if blur didn't change effective value
    if(nextLocalState[0] !== currentLocal[0] || nextLocalState[1] !== currentLocal[1]) {
       setLocalFounded(nextLocalState);
    }
  };

  // Ensure localFounded is always an array for rendering
  const displayFounded = Array.isArray(localFounded) ? localFounded : ["", ""];

  return (
    <div className={styles.accordionContent}>
      <label>Company founded between:</label>
      {/* Using headcountStyles as specified in original code */}
      <div className={headcountStyles.employeeHeadcountRange}>
        <div className={headcountStyles.headcountInputContainer}>
          <input
            type="text" // Keep as text to allow placeholder and partial input
            maxLength="4" // HTML attribute for input length limit
            // Use displayFounded which is guaranteed to be an array
            value={displayFounded[0]} // No comma formatting for year
            onChange={(e) => handleChange(e, 0)}
            onBlur={handleBlur}
            placeholder="YYYY" // Changed placeholder
          />
          <label>Min Year</label>
        </div>
        <div className={headcountStyles.headcountInputContainer}>
          <input
            type="text" // Keep as text
            maxLength="4"
            // Use displayFounded which is guaranteed to be an array
            value={displayFounded[1]} // No comma formatting for year
            onChange={(e) => handleChange(e, 1)}
            onBlur={handleBlur}
            placeholder="YYYY" // Changed placeholder
          />
          <label>Max Year</label>
        </div>
      </div>
    </div>
  );
};

export default FoundedFilter;